import { useEffect, useMemo, useRef, useState } from 'react'
import ExpandMore from '@mui/icons-material/ExpandMore'
import RuleOutlined from '@mui/icons-material/RuleOutlined'
import Search from '@mui/icons-material/Search'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import useUsersById from '@/hooks/useUsersById'
import { useGetApprovalPolicies } from '@/service-library/hooks/approval-policies'
import {
  useApplyApprovalPolicy,
  useGetDocumentApprovalState,
  useRemoveApprovalPolicy,
} from '@/service-library/hooks/document-approvals'
import { useGetPositions } from '@/service-library/hooks/positions'
import { ApprovalPolicy } from '@/types/approvals'
import { AuthUser } from '@/types/users'
import {
  isForbiddenError,
  isNotFoundError,
  isPolicyNotAttachableError,
  isStaleStateError,
} from '@/utils/approval-actions'
import {
  ApproverInfo,
  policyApproverRows,
  statePolicyApproverRows,
} from '@/utils/approver-entries'
import { orderChipLabel, policyOrderChipLabel } from '@/utils/policy-labels'
import { showErrorSnackbar, showWarningSnackbar } from '@/utils/snackbars'
import ApprovalPolicyInfo, {
  PolicyConditions,
} from '@/components/approvals/ApprovalPolicyInfo'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

/** Lines the details up under the policy name: the summary's own left
 *  padding (16px), the checkbox (42px), and the gap between them (8px). */
const DETAILS_INDENT = '66px'

type ManageApprovalPoliciesDialogProps = {
  documentId: string
  overlay: OverlayState
}

type PolicyRowData = {
  id: string
  description: string
  llmTriggerPrompt: string
  isEnabled: boolean
  orderLabel: string | null
  approvers: ApproverInfo[]
}

function LoadingSkeleton() {
  return (
    <Stack spacing={1.5}>
      {[0, 1, 2].map((row) => (
        <Stack key={row} direction="row" spacing={1.5} alignItems="center">
          <Skeleton variant="rounded" width={20} height={20} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            <Skeleton width="60%" />
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

type ErrorPanelProps = { onRetry: () => void }

function ErrorPanel({ onRetry }: ErrorPanelProps) {
  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      sx={{ textAlign: 'center', py: 3 }}
    >
      <Typography variant="body2">
        Couldn&apos;t load approval policies
      </Typography>
      <Button variant="outlined" size="small" onClick={onRetry}>
        Retry
      </Button>
    </Stack>
  )
}

function EmptyState() {
  return (
    <Stack
      alignItems="center"
      spacing={0.5}
      sx={{ textAlign: 'center', px: 2, py: 4 }}
    >
      <RuleOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        No approval policies yet
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Create one in project settings to require sign-off on this document.
      </Typography>
    </Stack>
  )
}

type PolicyLabelProps = {
  row: PolicyRowData
  note?: string
}

function PolicyLabel({ row, note }: PolicyLabelProps) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="body2">{row.description}</Typography>
        {!row.isEnabled && (
          <Typography variant="body2" color="text.secondary">
            (disabled)
          </Typography>
        )}
      </Stack>
      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}
    </Stack>
  )
}

type PolicyToggle = {
  checked: boolean
  onToggle: (checked: boolean) => void
}

type PolicyRowProps = {
  row: PolicyRowData
  toggle: PolicyToggle
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  usersById: Map<string, AuthUser>
  positionNamesById: Map<string, string>
  positionsPending: boolean
  usersPending: boolean
}

function PolicyRow({
  row,
  toggle,
  expanded,
  onExpandedChange,
  usersById,
  positionNamesById,
  positionsPending,
  usersPending,
}: PolicyRowProps) {
  // The server won't attach a policy that's switched off in project settings,
  // so once one is detached here there's no putting it back.
  const lockedOff = !row.isEnabled && !toggle.checked

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, isExpanded) => onExpandedChange(isExpanded)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        // Without this the row's name would absorb the checkbox's label.
        aria-label={`${expanded ? 'Hide' : 'Show'} details for ${
          row.description
        }`}
        sx={{
          '& .MuiAccordionSummary-content': {
            my: 0.5,
            minWidth: 0,
            alignItems: 'center',
            columnGap: 1,
          },
        }}
      >
        <Checkbox
          checked={toggle.checked}
          disabled={lockedOff}
          onChange={(_e, isChecked) => toggle.onToggle(isChecked)}
          // Stops a checkbox click from also expanding the row.
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          inputProps={{ 'aria-label': row.description }}
        />
        <PolicyLabel
          row={row}
          note={
            lockedOff
              ? 'Turned off in project settings. Turn it back on there to use it again.'
              : undefined
          }
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pl: DETAILS_INDENT, pt: 0 }}>
        <ApprovalPolicyInfo
          conditions={<PolicyConditions text={row.llmTriggerPrompt} />}
          orderLabel={row.orderLabel}
          approvers={row.approvers}
          usersById={usersById}
          positionNamesById={positionNamesById}
          positionsPending={positionsPending}
          usersPending={usersPending}
        />
      </AccordionDetails>
    </Accordion>
  )
}

type PolicyDialogBodyProps = {
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  rows: PolicyRowData[]
  isChecked: (policyId: string) => boolean
  onToggle: (policyId: string, checked: boolean) => void
  expandedPolicyIds: Set<string>
  onExpandedChange: (policyId: string, expanded: boolean) => void
  usersById: Map<string, AuthUser>
  positionNamesById: Map<string, string>
  positionsPending: boolean
  usersPending: boolean
  /** True while a search term is hiding rows, so an empty list can say "no matches" instead of "none exist". */
  isFiltered: boolean
}

function PolicyDialogBody({
  isLoading,
  isError,
  onRetry,
  rows,
  isChecked,
  onToggle,
  expandedPolicyIds,
  onExpandedChange,
  usersById,
  positionNamesById,
  positionsPending,
  usersPending,
  isFiltered,
}: PolicyDialogBodyProps) {
  if (isLoading) return <LoadingSkeleton />
  if (isError) return <ErrorPanel onRetry={onRetry} />
  if (rows.length === 0) {
    if (isFiltered) {
      return (
        <Typography variant="body2" color="text.secondary">
          No policies match your search.
        </Typography>
      )
    }
    return <EmptyState />
  }

  return (
    <Stack>
      {rows.map((row) => (
        <PolicyRow
          key={row.id}
          row={row}
          toggle={{
            checked: isChecked(row.id),
            onToggle: (checked) => onToggle(row.id, checked),
          }}
          expanded={expandedPolicyIds.has(row.id)}
          onExpandedChange={(expanded) => onExpandedChange(row.id, expanded)}
          usersById={usersById}
          positionNamesById={positionNamesById}
          positionsPending={positionsPending}
          usersPending={usersPending}
        />
      ))}
    </Stack>
  )
}

/** Every checkbox writes immediately — there's no save step. */
export default function ManageApprovalPoliciesDialog({
  documentId,
  overlay,
}: ManageApprovalPoliciesDialogProps) {
  const { project } = useProjectContext()
  const projectId = project.id
  const {
    approvalState,
    isFetching: approvalStateFetching,
    isError: approvalStateError,
    refetch: refetchApprovalState,
    queryKey: approvalStateQueryKey,
  } = useGetDocumentApprovalState({ id: documentId, enabled: overlay.isOpen })

  const {
    approvalPolicies,
    isFetching: policiesFetching,
    isError: policiesError,
    refetch: refetchPolicies,
  } = useGetApprovalPolicies({
    filters: { limit: '1000', project_id: projectId },
    enabled: overlay.isOpen,
  })

  const { usersById, isPending: usersPending } = useUsersById({
    enabled: overlay.isOpen,
  })

  // A policy's approver list can name a position instead of a person, and the
  // policy only carries its id.
  // Only fetched once a loaded policy actually names a position: the common
  // case is an all-user policy, which needs no catalog at all.
  const namesPosition = approvalPolicies.some((policy) =>
    policy.approvers.some(({ kind }) => kind === 'position'),
  )

  const { positions, isPending: positionsFetching } = useGetPositions({
    filters: { limit: '1000', project_id: projectId },
    enabled: overlay.isOpen && namesPosition,
  })

  const positionsPending = namesPosition && positionsFetching

  const positionNamesById = useMemo(
    () => new Map(positions.map(({ id, name }) => [id, name])),
    [positions],
  )

  // What the user has asked for but hasn't been written yet. Writes go one at
  // a time, so a box has to show the user's answer before its own write is
  // even sent.
  const [queuedToggles, setQueuedToggles] = useState<Map<string, boolean>>(
    () => new Map(),
  )

  // The last write in the queue, which answers with the state version the next
  // one has to send. Empty once every write has finished.
  const queuedWrites = useRef<Promise<string | undefined> | undefined>(
    undefined,
  )
  const queuedWriteCount = useRef(0)

  const attachedPolicyIds = useMemo(
    () =>
      new Set(
        approvalState?.policies.map((policy) => policy.approval_policy_id),
      ),
    [approvalState],
  )

  // A policy seen attached this session keeps its row even after it's
  // unchecked — otherwise the row, and the only way to recheck it, disappears.
  const [seenAttachedIds, setSeenAttachedIds] = useState<Set<string>>(
    () => new Set(),
  )

  const [expandedPolicyIds, setExpandedPolicyIds] = useState<Set<string>>(
    () => new Set(),
  )

  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    setSeenAttachedIds((prev) => (prev.size === 0 ? prev : new Set()))
    setExpandedPolicyIds((prev) => (prev.size === 0 ? prev : new Set()))
    setSearchTerm('')
  }, [overlay.isOpen, documentId])

  useEffect(() => {
    if (!overlay.isOpen) return
    setSeenAttachedIds((prev) => {
      const next = new Set(prev)
      attachedPolicyIds.forEach((id) => next.add(id))
      return next.size === prev.size ? prev : next
    })
  }, [overlay.isOpen, attachedPolicyIds])

  const setExpanded = (policyId: string, expanded: boolean) => {
    setExpandedPolicyIds((prev) => {
      const next = new Set(prev)
      if (expanded) {
        next.add(policyId)
      } else {
        next.delete(policyId)
      }
      return next
    })
  }

  const rows: PolicyRowData[] = useMemo(() => {
    const statePolicies = new Map(
      approvalState?.policies.map((policy) => [
        policy.approval_policy_id,
        policy,
      ]),
    )

    const buildRow = (id: string, policy?: ApprovalPolicy): PolicyRowData => {
      // An attached policy runs on the version pinned to the document, which
      // can differ from the project's current config — so its approvers, order
      // rule and decisions have to come from the approval state.
      const statePolicy = statePolicies.get(id)

      return {
        id,
        description:
          policy?.description ?? statePolicy?.description ?? 'Unknown policy',
        llmTriggerPrompt: policy?.llm_trigger_prompt ?? '',
        isEnabled: policy?.is_enabled ?? false,
        orderLabel:
          (statePolicy && orderChipLabel(statePolicy)) ??
          (policy ? policyOrderChipLabel(policy) : null),
        approvers: statePolicy
          ? statePolicyApproverRows(statePolicy)
          : policy
          ? policyApproverRows(policy)
          : [],
      }
    }

    const rowsById = new Map<string, PolicyRowData>()

    approvalPolicies.forEach((policy) =>
      rowsById.set(policy.id, buildRow(policy.id, policy)),
    )

    seenAttachedIds.forEach((id) => {
      if (rowsById.has(id)) return
      rowsById.set(
        id,
        buildRow(
          id,
          approvalPolicies.find((policy) => policy.id === id),
        ),
      )
    })

    return [...rowsById.values()]
  }, [approvalPolicies, seenAttachedIds, approvalState])

  const handleWriteError = (
    error: unknown,
    policyId: string,
    action: 'attach' | 'detach',
  ) => {
    if (isStaleStateError(error)) {
      showWarningSnackbar(
        'The document changed while you were updating it. The list below is now up to date — please try again.',
      )
      refetchApprovalState()
      return
    }

    // The server only refuses a policy this way on attach, where it checks the
    // policy is switched on and in this document's project. A 400 on detach
    // means something else went wrong.
    if (action === 'attach' && isPolicyNotAttachableError(error)) {
      showErrorSnackbar(
        "This policy can't be attached to this document. It may have been turned off or moved to another project.",
      )
      refetchPolicies()
      return
    }
    if (isForbiddenError(error)) {
      showErrorSnackbar(
        "You don't have permission to change this document's approval policies.",
      )
      return
    }
    if (isNotFoundError(error)) {
      showErrorSnackbar('This document no longer exists.')
      return
    }
    showErrorSnackbar(
      'Failed to update approval policies. Please try again later.',
    )
  }

  const { applyApprovalPolicyAsync } = useApplyApprovalPolicy({
    documentId,
    approvalStateQueryKey,
    // Without these the hook can't check the box until the write comes back.
    projectPolicies: approvalPolicies,
    onError: (error, variables) =>
      handleWriteError(error, variables.approval_policy_id, 'attach'),
  })

  const { removeApprovalPolicyAsync } = useRemoveApprovalPolicy({
    documentId,
    approvalStateQueryKey,
    onError: (error, variables) =>
      handleWriteError(error, variables.approval_policy_id, 'detach'),
  })

  /**
   * Every write has to say which state version it expects, and the server only
   * accepts the current one — so a write can't be sent until the one before it
   * comes back with the new version. The checkbox doesn't wait for any of it.
   */
  const handleToggle = (policyId: string, checked: boolean) => {
    const stateVersionId = approvalState?.state_version_id
    if (!stateVersionId) return

    setQueuedToggles((prev) => new Map(prev).set(policyId, checked))

    queuedWriteCount.current += 1
    const previousWrite =
      queuedWrites.current ??
      Promise.resolve<string | undefined>(stateVersionId)

    queuedWrites.current = previousWrite
      .then((versionId) => {
        // An earlier write failed, so the version this one would have sent
        // never happened. That failure already told the user; sending this
        // would just fail too.
        if (!versionId) return undefined

        const body = {
          expected_state_version_id: versionId,
          approval_policy_id: policyId,
        }
        const write = checked
          ? applyApprovalPolicyAsync(body)
          : removeApprovalPolicyAsync(body)

        return write.then((data) => data.approval_state.state_version_id)
      })
      // The hooks' onError already handled this one.
      .catch(() => undefined)
      .finally(() => {
        queuedWriteCount.current -= 1
        if (queuedWriteCount.current > 0) return
        // Nothing left in flight, so the cache — written or rolled back — is
        // the answer again.
        queuedWrites.current = undefined
        setQueuedToggles(new Map())
      })
  }

  // Skeletons cover a refetch, not just the first load: a click sends the
  // state version its row was rendered from, and a cached row's is stale.
  const isLoading = approvalStateFetching || policiesFetching
  const isError = approvalStateError || policiesError

  const handleRetry = () => {
    refetchApprovalState()
    refetchPolicies()
  }

  const query = searchTerm.trim().toLowerCase()
  const matchingRows = query
    ? rows.filter((row) => row.description.toLowerCase().includes(query))
    : rows

  return (
    <Dialog title="Approval Policies" maxWidth="sm" {...overlay}>
      <DialogContent>
        {/* Kept mounted through a background refetch — gating it on the
            skeleton would yank the box out from under whoever is typing. */}
        {!isError && rows.length > 0 && (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search policies"
            inputProps={{ 'aria-label': 'Search policies' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />
        )}
        <PolicyDialogBody
          isLoading={isLoading}
          isError={isError}
          onRetry={handleRetry}
          rows={matchingRows}
          isFiltered={!!query}
          isChecked={(policyId) =>
            queuedToggles.get(policyId) ?? attachedPolicyIds.has(policyId)
          }
          onToggle={handleToggle}
          expandedPolicyIds={expandedPolicyIds}
          onExpandedChange={setExpanded}
          usersById={usersById}
          positionNamesById={positionNamesById}
          positionsPending={positionsPending}
          usersPending={usersPending}
        />
      </DialogContent>
      <DialogFooter>
        <Button variant="text" onClick={overlay.close}>
          Done
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
